import { Button, Card, Collapse, Empty, Form, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  getRoadmap,
  getRoadmapStages,
  updateRoadmap,
} from "../../repository/data.repository";
import { Roadmap, RoadmapStage } from "../../repository/data.default";
import DynamicForm from "../../controls/DynamicForm";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import { QueryOperation } from "../../code/search-query/search-query";
import View from "../../controls/View";
import RoadmapStagesRail from "./RoadmapStagesRail";
import RoadmapStagePanel from "./RoadmapStagePanel";

export interface RoadmapManageProps {
  readonly open?: boolean;
  readonly uid?: string | null;
  readonly onClose: () => void;
}

const detailsSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        {
          type: "textbox",
          name: "name",
          label: "Name",
          rules: [{ required: true, message: "Name is required" }],
        },
        { type: "textbox", name: "shortDescription", label: "Short Description" },
        { type: "editorbox", name: "description", label: "Description", rows: 4 },
        { type: "editorbox", name: "stepsDescription", label: "Steps Description", rows: 4 },
      ],
    },
  ],
};

// Full-page master-detail builder for the whole roadmap aggregate (details + stages + requirements + files).
// Every child mutation persists immediately and reloads its own sub-list — no tree-wide bulk save.
function RoadmapManage(props: RoadmapManageProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [stages, setStages] = useState<RoadmapStage[]>([]);
  const [selectedStageUid, setSelectedStageUid] = useState<string | null>(null);

  const loadStages = useCallback(
    (uid: string) => {
      return getRoadmapStages({
        searchQuery: {
          conditions: [
            { field: "roadmapUid", values: [uid], operation: QueryOperation.Equal },
          ],
        },
        pageNumber: 0,
        pageSize: 1000,
        sortBy: "order",
        sortDescending: false,
      })
        .then((d) => {
          const own = d.data;
          setStages(own);
          setSelectedStageUid((prev) =>
            prev && own.some((s) => s.uid === prev) ? prev : own[0]?.uid ?? null,
          );
        })
        .catch(() => setStages([]));
    },
    [],
  );

  const load = useCallback(
    (uid: string) => {
      setLoading(true);
      return Promise.all([getRoadmap(uid), loadStages(uid)])
        .then(([r]) => {
          setRoadmap(r);
          form.setFieldsValue({
            name: r.name,
            shortDescription: r.shortDescription,
            description: r.description,
            stepsDescription: r.stepsDescription,
          });
        })
        .catch(() => setRoadmap(null))
        .finally(() => setLoading(false));
    },
    [form, loadStages],
  );

  useEffect(() => {
    if (props.open && props.uid) {
      load(props.uid);
    } else {
      setRoadmap(null);
      setStages([]);
      setSelectedStageUid(null);
    }
  }, [props.open, props.uid, load]);

  function onSaveDetails(): void {
    if (!roadmap) return;
    form.validateFields().then(() => {
      setSavingDetails(true);
      const v = form.getFieldsValue();
      updateRoadmap({
        uid: roadmap.uid,
        rowVersion: roadmap.rowVersion,
        name: v.name,
        shortDescription: v.shortDescription,
        description: v.description,
        stepsDescription: v.stepsDescription,
      })
        .then(() => getRoadmap(roadmap.uid).then(setRoadmap))
        .then(() => message.success("Roadmap details saved"))
        .catch(() => message.error("Couldn't save the roadmap details"))
        .finally(() => setSavingDetails(false));
    });
  }

  const selectedStage = stages.find((s) => s.uid === selectedStageUid) ?? null;

  return (
    <View
      open={props.open}
      loading={loading}
      title={roadmap ? `Manage Roadmap — ${roadmap.name}` : "Manage Roadmap"}
      onBack={props.onClose}
    >
      <div className="flex flex-col gap-3 p-2">
        <Collapse
          defaultActiveKey={[]}
          items={[
            {
              key: "details",
              label: "Roadmap Details",
              children: (
                <div className="flex flex-col gap-2">
                  <DynamicForm className="w-auto" form={form} schema={detailsSchema} />
                  <div className="flex flex-row justify-end">
                    <Button type="primary" loading={savingDetails} onClick={onSaveDetails}>
                      Save Details
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />

        <div className="flex flex-row gap-3">
          <Card className="w-80 shrink-0" styles={{ body: { padding: 12 } }}>
            <RoadmapStagesRail
              roadmapUid={props.uid ?? ""}
              stages={stages}
              selectedUid={selectedStageUid}
              onSelect={setSelectedStageUid}
              onChanged={() => (props.uid ? loadStages(props.uid) : undefined)}
            />
          </Card>
          <Card className="flex-auto" styles={{ body: { padding: 12 } }}>
            {selectedStage ? (
              <RoadmapStagePanel
                key={selectedStage.uid}
                stage={selectedStage}
                onChanged={() => (props.uid ? loadStages(props.uid) : undefined)}
              />
            ) : (
              <Empty
                className="py-12"
                description="Select a stage on the left, or add one to start building the journey"
              />
            )}
          </Card>
        </div>
      </div>
    </View>
  );
}

export default RoadmapManage;
